import {useEffect, useState} from 'react';
import {Modal, Spinner} from '@tyrannosaurustech/ui';

import {useGetVettingConfigQuery, useUpdateVettingConfigMutation} from '../../../redux/reduxQuery/vettingApi';
import {useMutationWrapper} from '../../../common/api';
import {VettingConfig, VetterOption, statusLabel} from './constants';

const SectionTitle = ({title, hint}: {title: string; hint?: string}) => (
  <div className="mb-2 mt-5 first:mt-0">
    <div className="text-sm font-bold">{title}</div>
    {hint && <div className="text-xs text-gray-500">{hint}</div>}
  </div>
);

export const ConfigModal = ({isOpen, onClose}: {isOpen: boolean; onClose: () => void}) => {
  const {data: configData} = useGetVettingConfigQuery(undefined, {
    skip: !isOpen,
  });
  const [updateConfig] = useUpdateVettingConfigMutation();
  const [isSaving, setIsSaving] = useState(false);

  const [trackedUserIds, setTrackedUserIds] = useState<string[]>([]);
  const [caseFromStatuses, setCaseFromStatuses] = useState<string[]>([]);
  const [caseToStatuses, setCaseToStatuses] = useState<string[]>([]);
  const [pendingStatuses, setPendingStatuses] = useState<string[]>([]);

  const config: VettingConfig | undefined = configData?.config;
  const statuses: string[] = configData?.statuses || [];
  const vetterOptions: VetterOption[] = configData?.vetterOptions || [];

  useEffect(() => {
    if (isOpen && config) {
      setTrackedUserIds(config.trackedUserIds);
      setCaseFromStatuses(config.caseFromStatuses);
      setCaseToStatuses(config.caseToStatuses);
      setPendingStatuses(config.pendingStatuses);
    }
  }, [isOpen, config]);

  const save = useMutationWrapper(updateConfig, 'Vetting settings saved', () => {
    setIsSaving(false);
    onClose();
  });

  const toggle = (list: string[], value: string) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  // A status is either completed or outstanding, never both - the outstanding list below
  // hides completed statuses, so drop them rather than leave them silently ticked.
  const toggleCaseToStatus = (status: string) => {
    const next = toggle(caseToStatuses, status);
    setCaseToStatuses(next);
    setPendingStatuses(pendingStatuses.filter((s) => !next.includes(s)));
  };

  const onConfirm = () => {
    setIsSaving(true);
    save({
      trackedUserIds,
      caseFromStatuses,
      caseToStatuses,
      pendingStatuses: pendingStatuses.filter((s) => !caseToStatuses.includes(s)),
    }).catch(() => setIsSaving(false));
  };

  return (
    <Modal
      swapButtons
      onConfirm={onConfirm}
      open={isOpen}
      title="Vetting Settings"
      confirmText="Save Changes"
      setOpen={onClose}
      loading={isSaving}
      buttonAlignment="right"
      titleClassName="text-left font-bold text-xl font-semibold not-italic capitalize"
      panelClassName="w-[600px] max-w-[1000px] max-h-[85vh] overflow-y-auto"
    >
      {!config ? (
        <div className="flex justify-center py-10">
          <Spinner colorClassName="text-primary" />
        </div>
      ) : (
        <div className="mt-2 mb-2 text-left">
          <SectionTitle title="Vetting team" hint="Whose completed and outstanding work is tracked on this dashboard." />
          <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 p-1">
            {vetterOptions.map((vetter) => (
              <label key={vetter.id} className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-purple-700"
                  checked={trackedUserIds.includes(vetter.id)}
                  onChange={() => setTrackedUserIds(toggle(trackedUserIds, vetter.id))}
                />
                <span className="text-sm font-medium">{vetter.name}</span>
                <span className="ml-auto text-xs text-gray-400">{vetter.email}</span>
              </label>
            ))}
            {!vetterOptions.length && <div className="px-3 py-2 text-sm text-gray-400">No vetters configured.</div>}
          </div>

          <SectionTitle
            title="Line item statuses — moved from"
            hint="Line items starting in one of these statuses count toward vetting work."
          />
          <div className="grid grid-cols-2 gap-1">
            {statuses.map((status) => (
              <label key={status} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-purple-700"
                  checked={caseFromStatuses.includes(status)}
                  onChange={() => setCaseFromStatuses(toggle(caseFromStatuses, status))}
                />
                <span className="text-sm">{statusLabel(status)}</span>
              </label>
            ))}
          </div>

          <SectionTitle title="Line item statuses — moved to" hint="Reaching one of these statuses counts as a completed vetting." />
          <div className="grid grid-cols-2 gap-1">
            {statuses.map((status) => (
              <label key={status} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-purple-700"
                  checked={caseToStatuses.includes(status)}
                  onChange={() => toggleCaseToStatus(status)}
                />
                <span className="text-sm">{statusLabel(status)}</span>
              </label>
            ))}
          </div>

          <SectionTitle title="Outstanding statuses" hint="Line items currently in these statuses count as pending vetting work." />
          <div className="grid grid-cols-2 gap-1">
            {statuses
              .filter((status) => !caseToStatuses.includes(status))
              .map((status) => (
                <label key={status} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-purple-700"
                    checked={pendingStatuses.includes(status)}
                    onChange={() => setPendingStatuses(toggle(pendingStatuses, status))}
                  />
                  <span className="text-sm">{statusLabel(status)}</span>
                </label>
              ))}
          </div>
        </div>
      )}
    </Modal>
  );
};
